import deepmerge from 'deepmerge';
import {useMemo} from 'react';
import {SchemeLink} from '../../../applink/SchemeLink';
import {getIsAgeRestricted} from '../../../common/getIsAgeRestricted';
import {t} from '../../../common/getTranslations';
import {formatWithCommas} from '../../../common/shared/utils/formatWithCommas';
import {twMerge} from '../../../common/twMerge';
import {EMPTY_LANDING_PRODUCT_TEXT_KEY} from '../../../domain/landing/constant';
import {
  CardViewItem as CardViewItemType,
  LandingType,
  NormalListViewItem as NormalListViewItemType,
  PosterViewItem as PosterViewItemType,
} from '../../../graphql/generated/types';
import {LOG_CLICK_EVENT} from '../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {useLogin} from '../../../user/useLogin.hook';
import {Grid} from '../../atoms/Grid/Grid';
import {InfinityScroll} from '../../atoms/InfinityScroll/InfinityScroll';
import {Loading} from '../../atoms/Loading/Loading';
import {TabPositionControl} from '../../atoms/TabPositionControl/TabPositionControl';
import {BelowDefaultTab} from '../../organisms/BelowDefaultTab/BelowDefaultTab';
import {BelowTagTab} from '../../organisms/BelowTagTab/BelowTagTab';
import {CardViewItem} from '../../organisms/CardViewItem/CardViewItem';
import {HeaderTitle} from '../../organisms/HeaderTitle/HeaderTitle';
import {NoItem} from '../../organisms/NoItem/NoItem';
import {NormalListViewItem} from '../../organisms/NormalListViewItem/NormalListViewItem';
import {PosterViewItem} from '../../organisms/PosterViewItem/PosterViewItem';
import {LandingListProps} from './LandingListProps.interface';

export function LandingList({
  title,
  themeKeywordList,
  selectedThemeKeywordUid,
  landingItemList,
  onClickThemeKeyword,
  isMobile,
  isWebView,
  landingType,
  isLoading,
  topImageUrl,
  totalCount,
  isEnd,
  onClickSingle,
  requestNextPageData,
}: LandingListProps) {
  const hasTopImage = !!topImageUrl;
  const {user} = useLogin();

  const currentKeywordName = useMemo(
    () =>
      themeKeywordList?.find(
        keyword => keyword.param === selectedThemeKeywordUid
      )?.name,
    [selectedThemeKeywordUid, themeKeywordList]
  );

  return (
    <div
      className={twMerge(
        'relative mx-auto mb-15pxr flex w-full flex-1 flex-col',
        !isMobile && 'mt-20pxr max-w-956pxr',
        hasTopImage && 'max-w-720pxr'
      )}
    >
      {hasTopImage && (
        <img src={topImageUrl} alt="상단 이미지" className="mb-24pxr w-full" />
      )}
      {title && !isMobile && (
        <HeaderTitle
          className={twMerge('px-15pxr', !themeKeywordList && 'mb-32pxr')}
        >
          {title}
        </HeaderTitle>
      )}
      {themeKeywordList && (
        <TabPositionControl
          depth={1}
          isMobile={isMobile}
          isWebView={isWebView}
          disabled={!isMobile || hasTopImage}
        >
          <BelowTagTab
            className={twMerge('px-10pxr', !isMobile && 'mb-32pxr')}
            tagItemList={themeKeywordList}
            selectedTagItemParam={selectedThemeKeywordUid}
            onClickTagItem={onClickThemeKeyword}
            {...putTiaraObjectProps({
              customProps: {
                page_keyword: currentKeywordName,
              },
            })}
          />
        </TabPositionControl>
      )}
      {totalCount !== undefined && themeKeywordList && (
        <TabPositionControl
          depth={2}
          isMobile={isMobile}
          isWebView={isWebView}
          disabled={!isMobile || hasTopImage}
        >
          <BelowDefaultTab className="px-15pxr">
            <BelowDefaultTab.TextLabel>
              {t('common_all_count', {
                values: {
                  value: t('landing_series_count', {
                    values: {value: formatWithCommas(totalCount)},
                  }),
                },
              })}
            </BelowDefaultTab.TextLabel>
          </BelowDefaultTab>
        </TabPositionControl>
      )}
      <InfinityScroll
        isLoading={isLoading}
        canLoadNextPage={!isLoading && !isEnd}
        requestNextPageData={requestNextPageData}
      >
        {landingItemList.length === 0 && (
          <NoItem isMobile={isMobile}>
            {t(EMPTY_LANDING_PRODUCT_TEXT_KEY)}
          </NoItem>
        )}
        {landingType === LandingType.SeriesPoster && (
          <div className="mx-15pxr my-10pxr">
            <Grid cols={isMobile ? 3 : 6} foldableCols={6} gapPx={4}>
              {(landingItemList as PosterViewItemType[]).map((item, idx) => {
                const {
                  id,
                  thumbnail,
                  badgeList,
                  ageGradeBadge,
                  statusBadge,
                  ageGrade,
                  subtitleList,
                  title: posterTitle,
                  discountRateText,
                } = item;
                const posterViewItemLogObj = {
                  click: {
                    layer1: '섹션',
                    ordnum: String(idx + 1),
                  },
                  customProps: {
                    page_keyword: currentKeywordName,
                  },
                };
                return (
                  <PosterViewItem
                    key={id}
                    posterSrc={thumbnail}
                    badgeList={badgeList}
                    ageGradeBadge={ageGradeBadge}
                    statusBadge={statusBadge}
                    subtitleList={subtitleList}
                    title={posterTitle || ''}
                    isAgeRestricted={getIsAgeRestricted({
                      age: user?.age,
                      authenticatedAt: user?.authenticatedAt,
                      ageGrade,
                      selfCensorship: item.selfCensorship,
                    })}
                    scheme={item.scheme}
                    onClickCapture={e => LOG_CLICK_EVENT('작품_클릭', e)}
                    {...putTiaraObjectProps(
                      item.eventLog
                        ? deepmerge(item.eventLog, posterViewItemLogObj)
                        : posterViewItemLogObj
                    )}
                    discountRateText={discountRateText}
                  />
                );
              })}
            </Grid>
          </div>
        )}
        {landingType === LandingType.SingleList && (
          <div className="my-5pxr">
            <Grid
              cols={isMobile || hasTopImage ? 1 : 2}
              tabletCols={hasTopImage ? 1 : 2}
            >
              {(landingItemList as NormalListViewItemType[]).map(
                (item, idx) => {
                  const normalListViewItemLogObj = {
                    click: {
                      layer1: '섹션',
                      ordnum: String(idx + 1),
                    },
                    customProps: {
                      page_keyword: currentKeywordName,
                    },
                  };
                  return (
                    <SchemeLink key={item.id} scheme={item.scheme || undefined}>
                      <NormalListViewItem
                        item={item}
                        isAgeRestricted={getIsAgeRestricted({
                          age: user?.age,
                          authenticatedAt: user?.authenticatedAt,
                          ageGrade: item.ageGrade,
                          selfCensorship: item.selfCensorship,
                        })}
                        onClickCapture={e => {
                          e.stopPropagation();
                          e.preventDefault();
                          LOG_CLICK_EVENT('회차_클릭', e);
                          onClickSingle && onClickSingle(item);
                        }}
                        {...putTiaraObjectProps(
                          item.eventLog
                            ? deepmerge(item.eventLog, normalListViewItemLogObj)
                            : normalListViewItemLogObj
                        )}
                      />
                    </SchemeLink>
                  );
                }
              )}
            </Grid>
          </div>
        )}
        {landingType === LandingType.SeriesList && (
          <div className="my-5pxr">
            <Grid
              cols={isMobile || hasTopImage ? 1 : 2}
              tabletCols={hasTopImage ? 1 : 2}
            >
              {(landingItemList as NormalListViewItemType[]).map(
                (item, idx) => {
                  const normalListViewItemLogObj = {
                    click: {
                      layer1: '섹션',
                      ordnum: String(idx + 1),
                    },
                    customProps: {
                      page_keyword: currentKeywordName,
                    },
                  };
                  return (
                    <SchemeLink key={item.id} scheme={item.scheme || undefined}>
                      <NormalListViewItem
                        borderRadius={3}
                        item={item}
                        isAgeRestricted={getIsAgeRestricted({
                          age: user?.age,
                          authenticatedAt: user?.authenticatedAt,
                          ageGrade: item.ageGrade,
                          selfCensorship: item.selfCensorship,
                        })}
                        onClickCapture={e => LOG_CLICK_EVENT('작품_클릭', e)}
                        {...putTiaraObjectProps(
                          item.eventLog
                            ? deepmerge(item.eventLog, normalListViewItemLogObj)
                            : normalListViewItemLogObj
                        )}
                      />
                    </SchemeLink>
                  );
                }
              )}
            </Grid>
          </div>
        )}
        {landingType === LandingType.SeriesCard && (
          <div className="mx-15pxr my-10pxr">
            <Grid cols={isMobile ? 3 : 6} foldableCols={6} gapPx={4}>
              {(landingItemList as CardViewItemType[]).map((item, idx) => {
                const {
                  id,
                  thumbnail,
                  badgeList,
                  ageGradeBadge,
                  statusBadge,
                  ageGrade,
                  subtitleList,
                  categoryType,
                  discountRateText,
                  title,
                  backgroundColor,
                  isBook,
                } = item;
                const cardViewItemLogObj = {
                  click: {
                    layer1: '섹션',
                    ordnum: String(idx + 1),
                  },
                  customProps: {
                    page_keyword: currentKeywordName,
                  },
                };
                return (
                  <CardViewItem
                    key={id}
                    categoryType={categoryType}
                    thumbnail={thumbnail}
                    badgeList={badgeList}
                    ageGradeBadge={ageGradeBadge}
                    statusBadge={statusBadge}
                    subtitleList={subtitleList}
                    discountRateText={discountRateText}
                    isAgeRestricted={getIsAgeRestricted({
                      age: user?.age,
                      authenticatedAt: user?.authenticatedAt,
                      ageGrade,
                      selfCensorship: item.selfCensorship,
                    })}
                    cardViewTitle={title}
                    backgroundColor={backgroundColor}
                    isBook={isBook}
                    scheme={item.scheme}
                    onClickCapture={e => LOG_CLICK_EVENT('작품_클릭', e)}
                    {...putTiaraObjectProps(
                      item.eventLog
                        ? deepmerge(item.eventLog, cardViewItemLogObj)
                        : cardViewItemLogObj
                    )}
                  />
                );
              })}
            </Grid>
          </div>
        )}
        {landingType === LandingType.SetList && (
          <div className="my-5pxr">
            <Grid
              cols={isMobile || hasTopImage ? 1 : 2}
              tabletCols={hasTopImage ? 1 : 2}
            >
              {(landingItemList as NormalListViewItemType[]).map(
                (item, idx) => {
                  const normalListViewItemLogObj = {
                    click: {
                      layer1: '섹션',
                      ordnum: String(idx + 1),
                    },
                  };
                  return (
                    <NormalListViewItem
                      key={item.id}
                      borderRadius={8}
                      item={item}
                      isAgeRestricted={getIsAgeRestricted({
                        age: user?.age,
                        authenticatedAt: user?.authenticatedAt,
                        ageGrade: item.ageGrade,
                        selfCensorship: item.selfCensorship,
                      })}
                      onClickCapture={e => LOG_CLICK_EVENT('세트상품_클릭', e)}
                      {...putTiaraObjectProps(
                        item.eventLog
                          ? deepmerge(item.eventLog, normalListViewItemLogObj)
                          : normalListViewItemLogObj
                      )}
                      isSet
                    />
                  );
                }
              )}
            </Grid>
          </div>
        )}
        {landingType === LandingType.SetPoster && (
          <div className="mx-11pxr my-10pxr">
            <Grid cols={isMobile ? 3 : 6} foldableCols={6} gapPx={4}>
              {(landingItemList as PosterViewItemType[]).map((item, idx) => {
                const {
                  id,
                  thumbnail,
                  badgeList,
                  ageGradeBadge,
                  statusBadge,
                  ageGrade,
                  subtitleList,
                  labelBadgeList,
                  discountRateText,
                  title: posterTitle,
                } = item;

                const posterViewItemLogObj = {
                  click: {
                    layer1: '섹션',
                    ordnum: String(idx + 1),
                  },
                };

                return (
                  <PosterViewItem
                    key={id}
                    posterSrc={thumbnail}
                    badgeList={badgeList}
                    ageGradeBadge={ageGradeBadge}
                    statusBadge={statusBadge}
                    subtitleList={subtitleList}
                    title={posterTitle || ''}
                    isAgeRestricted={getIsAgeRestricted({
                      age: user?.age,
                      authenticatedAt: user?.authenticatedAt,
                      ageGrade,
                      selfCensorship: item.selfCensorship,
                    })}
                    scheme={item.scheme}
                    labelBadgeList={labelBadgeList}
                    discountRateText={discountRateText}
                    onClickCapture={e => LOG_CLICK_EVENT('세트상품_클릭', e)}
                    {...putTiaraObjectProps(
                      item.eventLog
                        ? deepmerge(item.eventLog, posterViewItemLogObj)
                        : posterViewItemLogObj
                    )}
                    isSet
                  />
                );
              })}
            </Grid>
          </div>
        )}
        {isLoading && (
          <div className="flex-center-full">
            <Loading />
          </div>
        )}
      </InfinityScroll>
    </div>
  );
}
